import { useImperativeHandle, useRef, useState } from 'react';
import type { FormEvent, Ref } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgDataListSelect, NgModal } from '@/ui';
import type { NgModalHandle } from '@/ui';
import type { GeoPoint, IncidentAgency } from '@/signalr';
import {
  getAgencyDisplayName,
  notAlreadyInvolvedAgencies,
  orderRelatedAgencies,
  requestCollaboration,
} from '@ngsos/portal-core/commands';
import type { AgencyWithDistance } from '@ngsos/portal-core/commands';
import { getRelatedAgenciesForSharing } from '@ngsos/portal-core/agency';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';

const FORM_ID = 'add-agency-to-incident-form';

export type AddAgencyToIncidentDialogHandle = {
  showModal: () => void;
};

export type AddAgencyToIncidentDialogProps = {
  incidentId: string;
  lastCallerPosition: GeoPoint | null;
  involvedAgencies?: readonly IncidentAgency[];
  ref?: Ref<AddAgencyToIncidentDialogHandle>;
};

export function AddAgencyToIncidentDialog({
  incidentId,
  lastCallerPosition,
  involvedAgencies,
  ref,
}: AddAgencyToIncidentDialogProps) {
  const { t, i18n } = useTranslation();
  const modalRef = useRef<NgModalHandle>(null);
  const agencyConfiguration = useAgencyConfiguration();
  const relatedAgencies = getRelatedAgenciesForSharing(agencyConfiguration);

  const [selectedAgency, setSelectedAgency] = useState<AgencyWithDistance | null>(null);

  const notInvolvedAgencies = notAlreadyInvolvedAgencies(relatedAgencies, involvedAgencies);
  const orderedRelatedAgencies = orderRelatedAgencies(notInvolvedAgencies, lastCallerPosition);
  const noAgenciesToAdd = notInvolvedAgencies.length === 0;

  const language = i18n.language || 'en';

  useImperativeHandle(ref, () => ({
    showModal: () => {
      modalRef.current?.show();
    },
  }));

  const closeDialog = () => {
    modalRef.current?.hide();
  };

  const addAgencyToIncident = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (selectedAgency != null) {
      await requestCollaboration(incidentId, selectedAgency.agencyInfo.id);
      closeDialog();
    }
  };

  return (
    <NgModal
      ref={modalRef}
      header={t('AddAgencyToIncidentDialog_Header')}
      bodyTemplate={
        noAgenciesToAdd ? (
          <div className="border border-1 border-dark-subtle border-dashed p-3 text-black-50 bg-light small text-center">
            {t('AddAgencyToIncidentDialog_AllAgenciesAlreadyAdded')}
          </div>
        ) : (
          <form id={FORM_ID} onSubmit={(event) => void addAgencyToIncident(event)}>
            <div>
              <NgDataListSelect<AgencyWithDistance>
                items={orderedRelatedAgencies}
                value={selectedAgency ?? undefined}
                onValueChanged={(value) => setSelectedAgency(value ?? null)}
                textSelector={(agency) => getAgencyDisplayName(agency, language)}
                nullText={t('AddAgencyToIncidentDialog_SelectAgencyPlaceholderText')}
                label={t('AddAgencyToIncidentDialog_SelectAgencyToAddLabel')}
              />
            </div>
          </form>
        )
      }
      footerTemplate={
        <>
          {!noAgenciesToAdd && (
            <NgButton
              submitButton
              form={FORM_ID}
              icon="fal fa-share-from-square"
              text={t('AddAgencyToIncidentDialog_Button_AddAgency')}
              disabled={selectedAgency == null}
            />
          )}
          <NgButton text={t('Button_Close')} outline onClick={closeDialog} />
        </>
      }
    />
  );
}
